import { defineStore } from "pinia";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import type {
  ICheckoutResponseData,
  ICoupon,
  IGetApplicableCouponsResponse,
} from "~/types/checkout";
import type { IStateCity } from "~/types/common";
import type { ShippingDetailsModel } from "~/utils/validation/shippingDetailsSchema";

const getInitialData = (): ICheckoutResponseData["data"] => ({
  id: "",
  organization_id: "",
  user_id: "",
  first_name: "",
  last_name: "",
  email: "",
  mobile_number: "",
  address: "",
  city_name: "",
  state_name: "",
  country_id: "",
  state_id: "",
  city_id: "",
  pincode: "",
  total_denominations_to_be_redeemed: "",
  has_coupon: "",
});

const getInitialApplicableCoupons = (): ICoupon[] => [];

export const useCheckoutStore = defineStore("checkoutStore", {
  state: () => ({
    checkoutData: getInitialData(),
    applicableCoupons: getInitialApplicableCoupons(),
  }),
  actions: {
    setCheckoutData(data: ICheckoutResponseData["data"]) {
      this.checkoutData = data;
    },
    setShippingDetails(data: ShippingDetailsModel) {
      this.checkoutData.first_name = data.first_name;
      this.checkoutData.last_name = data.last_name;
      this.checkoutData.email = data.email;
      this.checkoutData.mobile_number = data.mobile_number;
      this.checkoutData.address = data.address;
      this.checkoutData.city_name = data.city_name;
      this.checkoutData.state_name = data.state_name;
      this.checkoutData.pincode = data.pincode;
      this.checkoutData.state_id = data.state_id;
      this.checkoutData.city_id = data.city_id;
    },
    setCheckoutFormState(data: IStateCity) {
      this.checkoutData.state_name = data.name;
      this.checkoutData.state_id = data.id;
    },
    setCheckoutFormCity(data: IStateCity) {
      this.checkoutData.city_name = data.name;
      this.checkoutData.city_id = data.id;
    },
    setApplicableCoupons(data: IGetApplicableCouponsResponse["data"]) {
      this.applicableCoupons = data.coupons;
    },
    clearCheckoutData() {
      this.checkoutData = getInitialData();
      this.applicableCoupons = getInitialApplicableCoupons();
    },
  },
  getters: {
    getOrderId: (state) => state.checkoutData.id,
    getCheckoutData: (state) => state.checkoutData,
    getApplicableCoupons: (state) => state.applicableCoupons,
    hasCoupon: (state) =>
      state.checkoutData.has_coupon === COMMON_CONSTANTS.POSITIVE_VALUE,
    getShippingDetails: (state) => {
      return {
        first_name: state.checkoutData.first_name,
        last_name: state.checkoutData.last_name,
        email: state.checkoutData.email,
        mobile_number: state.checkoutData.mobile_number,
        address: state.checkoutData.address,
        city_name: state.checkoutData.city_name,
        state_name: state.checkoutData.state_name,
        pincode: state.checkoutData.pincode,
        state_id: state.checkoutData.state_id,
        city_id: state.checkoutData.city_id,
      };
    },
    getIsProfileCompleted: (state) => {
      const details = {
        first_name: state.checkoutData.first_name,
        last_name: state.checkoutData.last_name,
        email: state.checkoutData.email,
        mobile_number: state.checkoutData.mobile_number,
        address: state.checkoutData.address,
        city_name: state.checkoutData.city_name,
        state_name: state.checkoutData.state_name,
        pincode: state.checkoutData.pincode,
        state_id: state.checkoutData.state_id,
        city_id: state.checkoutData.city_id,
      };
      return Object.values(details).every((field) => field !== "");
    },
  },
});
